import { getLanguage, type TranslateFn, type TranslationKey } from "./i18n/store.js";

/**
 * Formats a whole-cent amount as a dollar string, e.g. 4995 → "$49.95". Used for
 * real money (pack prices) — never for a wallet balance or an action's cost,
 * which are credits (see formatCredits).
 */
export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** A credit amount as a bare localized number, e.g. 4995 → "4 995" (for the balance chip). */
export function formatCreditNumber(credits: number, lang: string = getLanguage()): string {
  return Math.max(0, Math.round(credits)).toLocaleString(lang);
}

/**
 * A short credit amount for tight spots (the top-bar balance chip):
 * 950 → "950", 4995 → "4,9к" / "4.9k", 1 250 000 → "1,2 млн" / "1.2M".
 * Always rounded DOWN to one decimal so a balance is never overstated; the
 * exact figure belongs in the accessible name / tooltip next to it.
 */
export function formatCreditCompact(credits: number, t: TranslateFn, lang: string = getLanguage()): string {
  const count = Math.max(0, Math.floor(credits));
  if (count < 1000) return formatCreditNumber(count, lang);
  const oneDecimal = (value: number) => value.toLocaleString(lang, { maximumFractionDigits: 1 });
  if (count < 1_000_000) return t("credits.compactK", { n: oneDecimal(Math.floor(count / 100) / 10) });
  return t("credits.compactM", { n: oneDecimal(Math.floor(count / 100_000) / 10) });
}

/**
 * Formats a credit amount in running text with the word spelled out and
 * declined for the UI language, e.g. 1 → "1 кредит", 3 → "3 кредита",
 * 1000 → "1 000 кредитов" / "1,000 credits". The wallet stores whole cents of
 * list price and 1 credit = 1 cent, so the number is used as is. Credits are a
 * prepaid licence to use the service, never shown as money.
 */
export function formatCredits(credits: number, t: TranslateFn, lang: string = getLanguage()): string {
  const count = Math.max(0, Math.round(credits));
  return t(creditsKey(count, lang), { count: formatCreditNumber(count, lang) });
}

/**
 * Just the unit word for `credits`, declined to agree with it — "кредитов" for
 * 1 100, "credits" for 500 — for layouts that set the number apart from the
 * word (the pricing cards' big figure).
 */
export function formatCreditUnit(credits: number, t: TranslateFn, lang: string = getLanguage()): string {
  const count = Math.max(0, Math.round(credits));
  return t(creditsKey(count, lang), { count: "" }).trim();
}

function creditsKey(count: number, lang: string): TranslationKey {
  const form = new Intl.PluralRules(lang).select(count);
  return form === "one" ? "credits.one" : form === "few" ? "credits.few" : form === "many" ? "credits.many" : "credits.other";
}

/** Flat credit price of `operation` from the server's price table, or null. */
export function operationCredits(prices: Record<string, number> | null | undefined, operation: string): number | null {
  const price = prices?.[operation];
  return typeof price === "number" ? price : null;
}
